import { useState } from 'react';
import { Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const tabs = [
  { key: 'hot', label: '人气榜' },
  { key: 'update', label: '更新榜' },
  { key: 'new', label: '新作榜' },
] as const;

type TabKey = (typeof tabs)[number]['key'];

const rankBadge: Record<number, string> = {
  1: 'bg-accent-500 text-foreground-950',
  2: 'bg-primary-500 text-background-50',
  3: 'bg-secondary-500 text-background-50',
};

export default function Rank() {
  useSeo({
    title: '策驰漫画网追更榜 - 人气榜/更新榜/新作榜，抢先看最新话',
    description:
      '策驰漫画网追更榜汇集热门连载人气榜、更新榜与新作榜，抢先看最新话，同步更新进度与汉化进度实时排行。',
    keywords: '策驰漫画网,追更榜,人气榜,更新榜,新作榜,抢先看最新话,同步更新进度',
  });

  const [tab, setTab] = useState<TabKey>('hot');

  const list = [...comics].sort((a, b) => {
    if (tab === 'hot') return b.hot - a.hot;
    if (tab === 'update') return b.updateProgress - a.updateProgress || b.translateProgress - a.translateProgress;
    return a.totalChapters - b.totalChapters;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 pb-20 pt-28 md:px-6 md:pt-32">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '追更榜' }]} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl text-foreground-950 md:text-4xl">追更榜</h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
          抢先看热门连载最新话，人气、更新与汉化进度实时排行，看看大家都在追什么。
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex w-fit rounded-full bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-foreground-500">更新频率：每小时同步</p>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        {list.map((c, idx) => (
          <div
            key={c.id}
            className="flex items-center gap-4 border-b border-background-100 p-4 transition-colors last:border-0 hover:bg-background-100"
          >
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-base font-black ${
                rankBadge[idx + 1] || 'bg-background-100 text-foreground-500'
              }`}
            >
              {idx + 1}
            </span>
            <img
              src={c.cover}
              alt={`${c.title} 封面`}
              title={`${c.title} 追更进度`}
              className="h-20 w-14 shrink-0 rounded-md object-cover object-top"
            />
            <div className="min-w-0 flex-1">
              <Link to={`/comic/${c.id}`} className="block truncate text-sm font-bold text-foreground-950 hover:text-primary-600">
                {c.title}
              </Link>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="whitespace-nowrap rounded-full bg-background-100 px-2 py-0.5 text-foreground-600">
                  {c.categories.join('/')}
                </span>
                <span className="truncate">{c.latestChapter}</span>
              </div>
              <div className="mt-2 hidden items-center gap-3 sm:flex">
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <span>更新</span>
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
                    <div className="h-full rounded-full bg-secondary-500" style={{ width: `${c.updateProgress}%` }}></div>
                  </div>
                  <span className="font-bold text-secondary-700">{c.updateProgress}%</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <span>汉化</span>
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${c.translateProgress}%` }}></div>
                  </div>
                  <span className="font-bold text-primary-700">{c.translateProgress}%</span>
                </div>
              </div>
            </div>
            <div className="hidden shrink-0 items-center gap-1 text-sm font-bold text-foreground-800 sm:flex">
              <i className="ri-eye-line text-foreground-400"></i>
              {c.views}
            </div>
            <Link
              to={`/comic/${c.id}`}
              className="shrink-0 whitespace-nowrap rounded-full bg-primary-50 px-4 py-2 text-xs font-bold text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              抢先看
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}